import { Controller } from "@hotwired/stimulus";
import { getAllJSON, getJSON, listResults, sendJSON, errorDetail } from "../api.js";

// Zarządzanie pionami (staff): lista pionów, przełącznik trybu (open/restricted) z
// zabezpieczeniem "foot-gun" (restricted + 0 przypisań → wymaga jawnego potwierdzenia),
// rozwijanie panelu przypisań, dodawanie (szukaj org + POST) i usuwanie (DELETE) przypisań.
// Konsumuje: /api/staff/verticals/, /api/staff/vertical-assignments/, /api/staff/clients/.
export default class extends Controller {
  static targets = [
    "footgun",        // globalny panel ostrzeżenia foot-gun (jeden, ukryty domyślnie)
    "tableWrap",      // wrapper tabeli (do ukrycia podczas ładowania)
    "rows",           // kontener wierszy pionów
    "status",         // komunikaty błędów / stanu
    "verticalRow",    // pojedynczy wiersz pionu (dodawany dynamicznie)
    "modeBtn",        // przycisk przełączania trybu w wierszu
    "expandBtn",      // przycisk rozwijania panelu przypisań w wierszu
    "assignmentPanel",// panel przypisań (rozwijany, w wierszu)
    "orgSearch",      // input wyszukiwania org w panelu przypisań
    "assignmentList", // lista renderowanych przypisań w panelu
    "assignmentRow",  // pojedynczy wiersz przypisania (dodawany dynamicznie)
  ];
  static values = {
    listUrl: String,        // /api/staff/verticals/
    assignmentsUrl: String, // /api/staff/vertical-assignments/
    orgsUrl: String,        // /api/staff/clients/
  };

  connect() {
    this._pendingToggle = null; // {verticalId, access, rowEl} — oczekujące potwierdzenie foot-gun
    this._searchTimer = null;
    this._orgs = [];            // keszowany wynik getAllJSON(orgsUrl) — filtrowanie client-side
    if (this.listUrlValue) this.#load();
  }

  disconnect() {
    clearTimeout(this._searchTimer);
  }

  // ─── Lista pionów ─────────────────────────────────────────────────────────

  async #load() {
    try {
      const data = await getJSON(this.listUrlValue);
      this.#renderRows(listResults(data));
    } catch {
      this.#setStatus("Nie udało się załadować pionów. Odśwież stronę.");
    }
  }

  #renderRows(verticals) {
    this.rowsTarget.textContent = "";
    if (!verticals.length) {
      this.#setStatus("Brak pionów.");
      return;
    }
    this.#setStatus(null);
    const tpl = document.getElementById("management-verticals-row-template");
    for (const v of verticals) {
      const row = tpl.content.firstElementChild.cloneNode(true);
      row.querySelector('[data-field="name"]').textContent = v.name;
      row.querySelector('[data-field="slug"]').textContent = v.slug;
      row.querySelector('[data-field="mode"]').textContent =
        v.access === "restricted" ? "Zamknięty" : "Otwarty";
      row.querySelector('[data-field="count"]').textContent = String(
        v.assignment_count ?? 0,
      );
      row.querySelector('[data-field="active"]').textContent = v.is_active
        ? "aktywny"
        : "nieaktywny";

      // Zachowaj metadane na wierszu — używane przez toggleMode / expand
      row.dataset.verticalId = v.id;
      row.dataset.assignmentCount = String(v.assignment_count ?? 0);
      row.dataset.verticalAccess = v.access ?? "open";

      // Wstrzyknij id-param do przycisków (template nie zna id w czasie kompilacji)
      const modeBtn = row.querySelector(
        '[data-management-verticals-target="modeBtn"]',
      );
      if (modeBtn) modeBtn.dataset.managementVerticalsIdParam = v.id;

      const expandBtn = row.querySelector(
        '[data-management-verticals-target="expandBtn"]',
      );
      if (expandBtn) expandBtn.dataset.managementVerticalsIdParam = v.id;

      this.rowsTarget.appendChild(row);
    }
  }

  // ─── Przełączanie trybu ───────────────────────────────────────────────────

  toggleMode(event) {
    const { id } = event.params;
    const rowEl = event.currentTarget.closest(
      '[data-management-verticals-target="verticalRow"]',
    );
    if (!rowEl) return;

    const currentAccess = rowEl.dataset.verticalAccess ?? "open";
    const newAccess = currentAccess === "restricted" ? "open" : "restricted";
    const count = Number(rowEl.dataset.assignmentCount ?? 0);

    // FOOT-GUN: przejście do "restricted" przy 0 przypisaniach → ujawnij ostrzeżenie
    if (newAccess === "restricted" && count === 0) {
      this._pendingToggle = { verticalId: id, access: newAccess, rowEl };
      if (this.hasFootgunTarget) this.footgunTarget.hidden = false;
      return;
    }

    this.#sendToggle(id, newAccess, rowEl);
  }

  confirmModeToggle() {
    if (!this._pendingToggle) return;
    const { verticalId, access, rowEl } = this._pendingToggle;
    this._pendingToggle = null;
    if (this.hasFootgunTarget) this.footgunTarget.hidden = true;
    this.#sendToggle(verticalId, access, rowEl);
  }

  cancelModeToggle() {
    this._pendingToggle = null;
    if (this.hasFootgunTarget) this.footgunTarget.hidden = true;
  }

  async #sendToggle(id, access, rowEl) {
    const res = await sendJSON("PATCH", `${this.listUrlValue}${id}/`, { access });
    if (!res.ok) {
      this.#setStatus(
        errorDetail(res.data, "Nie udało się zmienić trybu dostępu."),
      );
      return;
    }
    // Aktualizuj wiersz lokalnie (bez pełnego przeładowania)
    if (rowEl) {
      rowEl.dataset.verticalAccess = access;
      const modeEl = rowEl.querySelector('[data-field="mode"]');
      if (modeEl) modeEl.textContent = access === "restricted" ? "Zamknięty" : "Otwarty";
      // Jeśli backend zwraca zaktualizowany count, użyj go
      if (res.data?.assignment_count != null) {
        rowEl.dataset.assignmentCount = String(res.data.assignment_count);
        const countEl = rowEl.querySelector('[data-field="count"]');
        if (countEl) countEl.textContent = String(res.data.assignment_count);
      }
    }
  }

  // ─── Rozwijanie panelu przypisań ──────────────────────────────────────────

  async toggleAssignments(event) {
    const { id } = event.params;
    const rowEl = event.currentTarget.closest(
      '[data-management-verticals-target="verticalRow"]',
    );
    if (!rowEl) return;
    const panel = rowEl.querySelector(
      '[data-management-verticals-target="assignmentPanel"]',
    );
    if (!panel) return;

    const isExpanded = !panel.hidden;
    panel.hidden = isExpanded;
    if (isExpanded) return; // zwinięcie — nic więcej

    await this.#loadAssignments(id, panel, rowEl);
  }

  async #loadAssignments(id, panel, rowEl) {
    const listEl = panel.querySelector(
      '[data-management-verticals-target="assignmentList"]',
    );
    if (listEl) listEl.textContent = "Ładowanie…";
    try {
      const data = await getJSON(`${this.assignmentsUrlValue}?vertical=${id}`);
      this.#renderAssignments(listResults(data), panel, rowEl, id);
    } catch {
      if (listEl) listEl.textContent = "Nie udało się załadować przypisań.";
    }
  }

  #renderAssignments(assignments, panel, rowEl, verticalId) {
    const listEl = panel.querySelector(
      '[data-management-verticals-target="assignmentList"]',
    );
    if (!listEl) return;
    listEl.textContent = "";
    const tpl = document.getElementById(
      "management-verticals-assignment-row-template",
    );
    for (const a of assignments) {
      const row = tpl.content.firstElementChild.cloneNode(true);
      row.querySelector('[data-field="org_name"]').textContent =
        a.org_name ?? a.organization_name ?? "—";
      row.dataset.assignmentId = a.id;

      // Wstrzyknij id-param przypisania do przycisku usuwania
      const removeBtn = row.querySelector(
        '[data-management-verticals-target~="removeBtn"], [data-action~="management-verticals#removeAssignment"]',
      );
      if (removeBtn) removeBtn.dataset.managementVerticalsIdParam = a.id;

      listEl.appendChild(row);
    }
  }

  // ─── Wyszukiwanie org + dodawanie przypisania ─────────────────────────────

  // UWAGA: /api/staff/clients/ nie dokumentuje parametru ?q= do filtrowania po nazwie.
  // Stosujemy getAllJSON + filtrowanie client-side (max 10 stron × 25 = 250 org).
  searchOrg(event) {
    clearTimeout(this._searchTimer);
    const input = event.currentTarget;
    this._searchTimer = setTimeout(() => this.#runOrgSearch(input), 200);
  }

  async #runOrgSearch(input) {
    const term = input.value.trim().toLowerCase();
    const panel = input.closest(
      '[data-management-verticals-target="assignmentPanel"]',
    );
    if (!panel) return;
    if (!term) {
      this.#hideOrgDropdown(panel);
      return;
    }
    // Leniwa inicjalizacja keszy org — jeden getAllJSON na sesję panelu
    if (!this._orgs.length) {
      try {
        this._orgs = await getAllJSON(this.orgsUrlValue);
      } catch {
        return;
      }
    }
    const matches = this._orgs.filter((o) =>
      (o.name ?? "").toLowerCase().includes(term),
    );
    this.#renderOrgDropdown(panel, input, matches);
  }

  #renderOrgDropdown(panel, input, orgs) {
    let dropdown = panel.querySelector('[data-role="org-dropdown"]');
    if (!dropdown) {
      dropdown = document.createElement("ul");
      dropdown.dataset.role = "org-dropdown";
      input.insertAdjacentElement("afterend", dropdown);
    }
    dropdown.textContent = "";
    for (const org of orgs) {
      const li = document.createElement("li");
      const btn = document.createElement("button");
      btn.type = "button";
      btn.textContent = org.name;
      btn.addEventListener("mousedown", (e) => {
        e.preventDefault(); // zapobiega utracie focusu przez input
        this.#selectOrg(panel, org, input, dropdown);
      });
      li.appendChild(btn);
      dropdown.appendChild(li);
    }
    dropdown.hidden = orgs.length === 0;
  }

  #hideOrgDropdown(panel) {
    const dropdown = panel.querySelector('[data-role="org-dropdown"]');
    if (dropdown) {
      dropdown.hidden = true;
      dropdown.textContent = "";
    }
  }

  async #selectOrg(panel, org, input, dropdown) {
    const rowEl = panel.closest(
      '[data-management-verticals-target="verticalRow"]',
    );
    const verticalId = Number(rowEl?.dataset.verticalId);
    if (!verticalId) return;

    input.value = "";
    dropdown.hidden = true;
    dropdown.textContent = "";

    const res = await sendJSON("POST", this.assignmentsUrlValue, {
      vertical: verticalId,
      organization: org.id,
    });

    const errorEl = this.#panelError(panel);

    if (!res.ok) {
      // Duplikat: 400 {error:{message:…}}
      const msg =
        res.data?.error?.message ??
        errorDetail(res.data, "Nie udało się dodać przypisania.");
      errorEl.textContent = msg;
      errorEl.hidden = false;
      return;
    }

    errorEl.hidden = true;
    errorEl.textContent = "";

    // Dołącz nowy wiersz przypisania
    const listEl = panel.querySelector(
      '[data-management-verticals-target="assignmentList"]',
    );
    if (listEl) {
      const tpl = document.getElementById(
        "management-verticals-assignment-row-template",
      );
      const row = tpl.content.firstElementChild.cloneNode(true);
      row.querySelector('[data-field="org_name"]').textContent = org.name;
      row.dataset.assignmentId = res.data?.id ?? "";
      const removeBtn = row.querySelector(
        '[data-management-verticals-target~="removeBtn"], [data-action~="management-verticals#removeAssignment"]',
      );
      if (removeBtn) removeBtn.dataset.managementVerticalsIdParam = res.data?.id ?? "";
      listEl.appendChild(row);
    }

    // Zwiększ licznik przypisań w wierszu pionu
    if (rowEl) {
      const prev = Number(rowEl.dataset.assignmentCount ?? 0);
      const next = prev + 1;
      rowEl.dataset.assignmentCount = String(next);
      const countEl = rowEl.querySelector('[data-field="count"]');
      if (countEl) countEl.textContent = String(next);
    }
  }

  // ─── Usuwanie przypisania ─────────────────────────────────────────────────

  async removeAssignment(event) {
    const { id } = event.params; // id przypisania
    // Znajdź wiersz przypisania przez atrybut dataset (bardziej niezawodne niż target)
    const assignmentRowEl = event.currentTarget.closest("[data-assignment-id]");
    const rowEl = assignmentRowEl?.closest(
      '[data-management-verticals-target="verticalRow"]',
    );

    const res = await sendJSON(
      "DELETE",
      `${this.assignmentsUrlValue}${id}/`,
      undefined,
    );
    if (!res.ok) {
      this.#setStatus(
        errorDetail(res.data, "Nie udało się usunąć przypisania."),
      );
      return;
    }

    // Usuń wiersz z DOM
    if (assignmentRowEl) assignmentRowEl.remove();

    // Zmniejsz licznik
    if (rowEl) {
      const prev = Number(rowEl.dataset.assignmentCount ?? 0);
      const next = Math.max(0, prev - 1);
      rowEl.dataset.assignmentCount = String(next);
      const countEl = rowEl.querySelector('[data-field="count"]');
      if (countEl) countEl.textContent = String(next);
    }
  }

  // ─── Pomocnicze ──────────────────────────────────────────────────────────

  #setStatus(message) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.hidden = message === null;
    if (message !== null) this.statusTarget.textContent = message;
  }

  // Zwraca (lub tworzy) element błędu wewnątrz panelu przypisań.
  #panelError(panel) {
    let el = panel.querySelector("[data-role='panel-error']");
    if (!el) {
      el = document.createElement("p");
      el.dataset.role = "panel-error";
      el.hidden = true;
      panel.prepend(el);
    }
    return el;
  }
}
